import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import { AlertTriangle, Copy, QrCode, Smartphone } from "lucide-react";
import { channels } from "../../../shared/constants";

const { ipcRenderer } = window.require("electron");

const formatPairingCode = (code) => {
	if (!code) return "";
	const clean = String(code).replace(/\s|-/g, "");
	if (clean.length !== 8) return clean;
	return `${clean.slice(0, 4)}-${clean.slice(4)}`;
};

const sanitizePhone = (value) => value.replace(/\D/g, "").slice(0, 15);

export const QRCodeDialog = ({ isOpen, onClose, qrCode, pairingCode, pairingError, instanceId }) => {
	const [loadingTime, setLoadingTime] = useState(0);
	const [mode, setMode] = useState("qr");
	const [phoneInput, setPhoneInput] = useState("");
	const [qrRequested, setQrRequested] = useState(false);
	const [pairingRequested, setPairingRequested] = useState(false);
	const [copied, setCopied] = useState(false);

	const formattedCode = useMemo(() => formatPairingCode(pairingCode), [pairingCode]);

	useEffect(() => {
		let timer;
		const waitingQr = mode === "qr" && qrRequested && !qrCode;
		const waitingPairing = mode === "pairing" && pairingRequested && !pairingCode && !pairingError;

		if (isOpen && (waitingQr || waitingPairing)) {
			timer = setInterval(() => {
				setLoadingTime((prev) => prev + 1);
			}, 1000);
		} else {
			setLoadingTime(0);
		}

		return () => {
			if (timer) clearInterval(timer);
		};
	}, [isOpen, qrCode, pairingCode, pairingError, mode, qrRequested, pairingRequested]);

	useEffect(() => {
		if (!isOpen) {
			setQrRequested(false);
			setPairingRequested(false);
			setPhoneInput("");
			setCopied(false);
			setMode("qr");
		}
	}, [isOpen]);

	const handleStartQr = () => {
		ipcRenderer.send(channels.START_WHATS, instanceId);
		setQrRequested(true);
	};

	const handleStartPairing = () => {
		const sanitized = sanitizePhone(phoneInput);
		if (sanitized.length < 10) return;
		ipcRenderer.send(channels.START_WHATS_PAIRING, {
			instanceId,
			phoneNumber: sanitized,
		});
		setPairingRequested(true);
	};

	const handleCopyCode = async () => {
		if (!pairingCode) return;
		try {
			await navigator.clipboard.writeText(String(pairingCode).replace(/\s|-/g, ""));
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			console.error("Erro ao copiar código:", error);
		}
	};

	const handleLogout = async () => {
		try {
			await ipcRenderer.invoke(channels.LOGOUT_INSTANCE, instanceId);
			setLoadingTime(0);
			setQrRequested(false);
			setPairingRequested(false);
		} catch (error) {
			console.error("Erro ao fazer logout:", error);
		}
	};

	const phoneValid = sanitizePhone(phoneInput).length >= 10;
	const canSwitchMode = !qrRequested && !pairingRequested;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				className="max-w-md w-full mx-4 bg-gradient-to-br from-slateBase/90 via-slateCard/85 to-slateBase/90 backdrop-blur-2xl border border-whiteBase/20 shadow-2xl shadow-emeraldPrimary/10 p-0 overflow-hidden relative"
				style={{
					position: "fixed",
					top: "80px",
					left: "50%",
					transform: "translateX(-50%)",
					zIndex: 9999,
				}}
			>
				<div className="absolute inset-0 bg-gradient-to-br from-emeraldPrimary/5 via-transparent to-cyanPrimary/5 pointer-events-none"></div>
				<div className="absolute -top-10 -left-10 w-32 h-32 bg-emeraldMedium/10 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
				<div className="absolute -bottom-10 -right-10 w-40 h-40 bg-cyanTertiary/10 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: "1s" }}></div>

				<div className="relative flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-sm border-b border-whiteBase/10">
					<div className="flex items-center space-x-3">
						<div className="relative">
							<div className="absolute inset-0 bg-gradient-to-r from-emeraldPrimary to-cyanPrimary rounded-lg blur-sm opacity-30"></div>
							<div className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emeraldPrimary/20 to-cyanPrimary/20 backdrop-blur-sm border border-emeraldMedium/30 shadow-lg">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-emeraldLight">
									<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.386" fill="currentColor" />
								</svg>
							</div>
						</div>
						<div>
							<h2 className="text-lg font-bold text-textPrimary mb-1">Conectar ao WhatsApp</h2>
							<p className="text-xs text-grayText">Escolha como vincular seu dispositivo</p>
						</div>
					</div>
					<div className="w-2 h-2 rounded-full bg-gradient-to-r from-emeraldMedium to-cyanTertiary animate-pulse"></div>
				</div>

				<div className="relative flex gap-2 p-2 bg-whiteBase/5 border-b border-whiteBase/10">
					<button
						type="button"
						onClick={() => canSwitchMode && setMode("qr")}
						disabled={!canSwitchMode && mode !== "qr"}
						className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
							mode === "qr"
								? "bg-gradient-to-r from-emeraldPrimary/30 to-cyanPrimary/30 text-textPrimary border border-emeraldMedium/40 shadow-md"
								: "text-textSecondary hover:text-textPrimary hover:bg-whiteBase/5 disabled:opacity-40 disabled:cursor-not-allowed"
						}`}
					>
						<QrCode className="w-4 h-4" />
						QR Code
					</button>
					<button
						type="button"
						onClick={() => canSwitchMode && setMode("pairing")}
						disabled={!canSwitchMode && mode !== "pairing"}
						className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
							mode === "pairing"
								? "bg-gradient-to-r from-emeraldPrimary/30 to-cyanPrimary/30 text-textPrimary border border-emeraldMedium/40 shadow-md"
								: "text-textSecondary hover:text-textPrimary hover:bg-whiteBase/5 disabled:opacity-40 disabled:cursor-not-allowed"
						}`}
					>
						<Smartphone className="w-4 h-4" />
						Código de pareamento
					</button>
				</div>

				<div className="relative p-4 bg-whiteBase/5 backdrop-blur-sm">
					{mode === "qr" ? (
						<QrTab
							qrCode={qrCode}
							qrRequested={qrRequested}
							onStartQr={handleStartQr}
							loadingTime={loadingTime}
							onLogout={handleLogout}
						/>
					) : (
						<PairingTab
							phoneInput={phoneInput}
							setPhoneInput={setPhoneInput}
							onStartPairing={handleStartPairing}
							phoneValid={phoneValid}
							pairingRequested={pairingRequested}
							pairingCode={pairingCode}
							formattedCode={formattedCode}
							pairingError={pairingError}
							onCopy={handleCopyCode}
							copied={copied}
							loadingTime={loadingTime}
							onLogout={handleLogout}
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

const QrTab = ({ qrCode, qrRequested, onStartQr, loadingTime, onLogout }) => {
	if (!qrRequested) {
		return (
			<div className="space-y-4">
				<div className="text-center">
					<p className="text-base font-medium text-textPrimary mb-1">Conectar via QR Code</p>
					<p className="text-textSecondary text-xs">Escaneie o código com a câmera do WhatsApp</p>
				</div>
				<button
					type="button"
					onClick={onStartQr}
					className="w-full rounded-xl bg-gradient-to-r from-emeraldBase to-cyanPrimary hover:from-emeraldMedium hover:to-cyanTertiary text-textOnDarkStrong px-6 py-3 font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-emeraldMedium/30"
				>
					Gerar QR Code
				</button>
			</div>
		);
	}

	if (qrCode) {
		return (
			<div className="space-y-6">
				<div className="text-center">
					<p className="text-base font-medium text-textPrimary mb-1">Quase lá!</p>
					<p className="text-textSecondary text-xs">Escaneie o QR Code para conectar ao WhatsApp</p>
				</div>
				<div className="flex justify-center">
					<div className="relative group">
						<div className="absolute -inset-4 bg-gradient-to-r from-emeraldPrimary/20 via-cyanPrimary/20 to-emeraldPrimary/20 rounded-3xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
						<div className="relative bg-gradient-to-br from-whiteBase via-whiteBase to-graySubtle p-4 rounded-xl shadow-2xl border border-whiteBase/50 backdrop-blur-sm">
							<img src={qrCode} alt="QR Code" className="size-48 relative z-10 rounded-lg" />
						</div>
					</div>
				</div>
				<InstructionsCard
					title="Como conectar"
					steps={[
						<>Abra o <span className="text-emeraldLight font-medium">WhatsApp</span> no seu celular</>,
						<>Vá em <span className="text-emeraldLight font-medium">Configurações</span> → <span className="text-emeraldLight font-medium">Dispositivos conectados</span></>,
						<>Toque em <span className="text-emeraldLight font-medium">Conectar um dispositivo</span> e escaneie o código</>,
					]}
				/>
			</div>
		);
	}

	return (
		<LoadingBlock
			title="Conectando ao WhatsApp"
			subtitle="Preparando QR Code..."
			loadingTime={loadingTime}
			warningMessage="Estamos tentando gerar o QR Code mas está demorando mais que o esperado."
			errorMessage="Parece que estamos com problemas para gerar o QR Code. Tente fazer logout para reiniciar a conexão."
			onLogout={onLogout}
		/>
	);
};

const PairingTab = ({
	phoneInput,
	setPhoneInput,
	onStartPairing,
	phoneValid,
	pairingRequested,
	pairingCode,
	formattedCode,
	pairingError,
	onCopy,
	copied,
	loadingTime,
	onLogout,
}) => {
	if (!pairingRequested) {
		return (
			<div className="space-y-4">
				<div className="text-center">
					<p className="text-base font-medium text-textPrimary mb-1">Conectar via código</p>
					<p className="text-textSecondary text-xs">Digite seu número com DDI e DDD (apenas dígitos)</p>
				</div>
				<div className="space-y-2">
					<label className="text-xs text-textSecondary" htmlFor="pairing-phone-input">
						Número de telefone
					</label>
					<input
						id="pairing-phone-input"
						type="tel"
						inputMode="numeric"
						autoComplete="off"
						placeholder="5511999999999"
						value={phoneInput}
						onChange={(e) => setPhoneInput(sanitizePhone(e.target.value))}
						className="w-full px-4 py-3 rounded-xl bg-whiteBase/10 border border-whiteBase/20 text-textPrimary placeholder:text-textSecondary/50 focus:outline-none focus:ring-2 focus:ring-emeraldMedium/50 focus:border-emeraldMedium/50 transition-all"
					/>
					<p className="text-[11px] text-textSecondary/70">
						Exemplo: <span className="text-emeraldLight">55</span> (Brasil) + <span className="text-emeraldLight">11</span> (DDP) + número.
					</p>
				</div>
				<button
					type="button"
					onClick={onStartPairing}
					disabled={!phoneValid}
					className="w-full rounded-xl bg-gradient-to-r from-emeraldBase to-cyanPrimary hover:from-emeraldMedium hover:to-cyanTertiary text-textOnDarkStrong px-6 py-3 font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-emeraldMedium/30 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Gerar código de pareamento
				</button>
			</div>
		);
	}

	if (pairingCode) {
		return (
			<div className="space-y-6">
				<div className="text-center">
					<p className="text-base font-medium text-textPrimary mb-1">Seu código está pronto!</p>
					<p className="text-textSecondary text-xs">Digite no WhatsApp do seu celular em até 60 segundos</p>
				</div>

				<div className="flex justify-center">
					<button
						type="button"
						onClick={onCopy}
						className="relative group flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-br from-whiteBase/15 via-whiteBase/10 to-whiteBase/15 border border-emeraldMedium/40 shadow-xl shadow-emeraldPrimary/20 hover:border-emeraldMedium/70 transition-all"
					>
						<div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emeraldPrimary/10 to-cyanPrimary/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
						<span className="relative text-3xl font-mono font-bold tracking-[0.3em] text-textPrimary">
							{formattedCode}
						</span>
						<Copy className="relative w-5 h-5 text-textSecondary group-hover:text-emeraldLight transition-colors" />
					</button>
				</div>
				{copied && (
					<p className="text-center text-xs text-emeraldLight">Código copiado!</p>
				)}

				<InstructionsCard
					title="Como inserir o código"
					steps={[
						<>Abra o <span className="text-emeraldLight font-medium">WhatsApp</span> no seu celular</>,
						<>Vá em <span className="text-emeraldLight font-medium">Configurações</span> → <span className="text-emeraldLight font-medium">Dispositivos conectados</span></>,
						<>Toque em <span className="text-emeraldLight font-medium">Conectar um dispositivo</span> → <span className="text-emeraldLight font-medium">Conectar com número de telefone</span></>,
						<>Digite o código <span className="text-emeraldLight font-medium">{formattedCode}</span></>,
					]}
				/>
			</div>
		);
	}

	if (pairingError) {
		return (
			<div className="relative p-6 rounded-2xl bg-gradient-to-br from-statusError/10 via-statusError/5 to-statusError/10 border border-statusError/30">
				<div className="flex items-start gap-4">
					<AlertTriangle className="h-5 w-5 text-statusError flex-shrink-0 mt-0.5" />
					<div className="flex-1">
						<h4 className="text-statusErrorLight font-semibold mb-2">Erro ao gerar código</h4>
						<p className="text-statusErrorLight text-sm leading-relaxed">{pairingError}</p>
						<button
							type="button"
							onClick={onLogout}
							className="mt-4 w-full rounded-xl bg-statusError/20 hover:bg-statusError/30 border border-statusError/40 px-4 py-2 text-sm font-medium text-statusErrorLight transition-all"
						>
							Reiniciar conexão
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<LoadingBlock
			title="Solicitando código"
			subtitle="Gerando código de pareamento..."
			loadingTime={loadingTime}
			warningMessage="Estamos tentando gerar o código mas está demorando mais que o esperado."
			errorMessage="Não foi possível gerar o código de pareamento. Tente fazer logout para reiniciar."
			onLogout={onLogout}
		/>
	);
};

const LoadingBlock = ({ title, subtitle, loadingTime, warningMessage, errorMessage, onLogout }) => (
	<div className="space-y-6">
		<div className="flex flex-col items-center space-y-4 py-8">
			<div className="w-20 h-20 rounded-full bg-emeraldPrimary/20 border-2 border-emeraldMedium/40 flex items-center justify-center animate-pulse">
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-emeraldLight">
					<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.386" fill="currentColor" />
				</svg>
			</div>
			<div className="text-center">
				<h3 className="text-lg font-semibold text-textPrimary mb-1">{title}</h3>
				<p className="text-grayText text-sm">{subtitle}</p>
			</div>
		</div>

		{loadingTime >= 10 && loadingTime < 30 && (
			<div className="relative p-6 rounded-2xl bg-gradient-to-br from-statusWarning/10 via-statusWarning/5 to-statusWarning/10 border border-statusWarning/30">
				<div className="flex items-start gap-4">
					<AlertTriangle className="h-5 w-5 text-statusWarning flex-shrink-0 mt-0.5" />
					<div>
						<h4 className="text-statusWarning font-semibold mb-2">Está demorando mais que o normal</h4>
						<p className="text-statusYellowLighter text-sm">{warningMessage}</p>
					</div>
				</div>
			</div>
		)}

		{loadingTime >= 30 && (
			<div className="relative p-6 rounded-2xl bg-gradient-to-br from-statusError/10 via-statusError/5 to-statusError/10 border border-statusError/30">
				<div className="flex items-start gap-4">
					<AlertTriangle className="h-5 w-5 text-statusError flex-shrink-0 mt-0.5" />
					<div className="flex-1">
						<h4 className="text-statusErrorLight font-semibold mb-2">Problema na conexão</h4>
						<p className="text-statusErrorLight text-sm">{errorMessage}</p>
						<button
							type="button"
							onClick={onLogout}
							className="mt-4 w-full rounded-xl bg-statusError/20 hover:bg-statusError/30 border border-statusError/40 px-4 py-2 text-sm font-medium text-statusErrorLight transition-all"
						>
							Fazer Logout e Tentar Novamente
						</button>
					</div>
				</div>
			</div>
		)}
	</div>
);

const InstructionsCard = ({ title, steps }) => (
	<div className="relative p-4 rounded-xl bg-gradient-to-br from-whiteBase/10 via-whiteBase/5 to-whiteBase/10 backdrop-blur-xl border border-whiteBase/20 shadow-lg">
		<div className="absolute inset-0 bg-gradient-to-r from-emeraldPrimary/5 to-cyanPrimary/5 rounded-xl"></div>
		<div className="relative z-10">
			<div className="flex items-center space-x-2 mb-3">
				<div className="w-6 h-6 rounded-lg bg-gradient-to-r from-emeraldPrimary/20 to-cyanPrimary/20 flex items-center justify-center">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-emeraldLight">
						<path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor" />
					</svg>
				</div>
				<h3 className="text-textPrimary font-semibold text-sm">{title}</h3>
			</div>
			<div className="space-y-2 text-xs text-textSecondary leading-relaxed">
				{steps.map((step, idx) => (
					<div key={idx} className="flex items-start space-x-2">
						<div className="w-5 h-5 rounded-full bg-emeraldPrimary/20 border border-emeraldMedium/30 flex items-center justify-center flex-shrink-0 mt-0.5">
							<span className="text-emeraldLight text-xs font-bold">{idx + 1}</span>
						</div>
						<p>{step}</p>
					</div>
				))}
			</div>
		</div>
	</div>
);
